<template>
  <Teleport to="body">
    <div v-if="show" class="dca eh-ds" data-cy="code-assigner">
      <!-- ===== header ===== -->
      <div class="dca-header">
        <div class="dca-header-title">
          <span class="glyphicon dca-header-icon" :class="isReview ? 'glyphicon-file bi bi-file-earmark-text' : 'glyphicon-pencil bi bi-pen'"></span>
          <div class="dca-header-text">
            <div class="dca-title">{{ headerTitle }}</div>
            <!-- Full identity (name · MRN · DOB) when the parent supplies it. -->
            <div class="dca-subtitle" data-cy="modal-patient">{{ patientLine || patientName }}<template v-if="recordLabel"> · {{ recordLabel }}</template></div>
          </div>
        </div>
        <button class="dca-close" aria-label="Close" @click="onCancel">
          <span class="glyphicon glyphicon-remove bi bi-x-lg"></span>
        </button>
      </div>

      <!-- ===== mobile tabs ===== -->
      <div class="dca-tabs">
        <button class="dca-tab" :class="{ active: mobileTab === 'document' }" @click="mobileTab = 'document'">Document</button>
        <button class="dca-tab" :class="{ active: mobileTab === 'codes' }" @click="mobileTab = 'codes'">
          {{ isReview ? 'Directives' : (isAdd ? 'Add codes' : 'Assign codes') }}<span v-if="!isReview && selectedCount" class="dca-tab-badge">{{ selectedCount }}</span>
        </button>
      </div>

      <!-- ===== body ===== -->
      <div ref="splitRef" class="dca-body">
        <!-- LEFT: codes pane -->
        <div
          class="dca-pane-codes"
          :class="{ 'dca-mobile-hidden': mobileTab !== 'codes' }"
          :style="{ '--dca-left': leftPct + '%' }"
        >
          <div class="dca-pane-codes-head">
            <div class="dca-instruction">{{ instruction }}</div>
            <div v-if="isReview" class="dca-approver-line">{{ approverLine }}</div>
          </div>

          <div class="dca-pane-codes-scroll">
            <!-- add mode: what the record already carries, so the reviewer is
                 adding TO a known set rather than guessing. Read-only — existing
                 codes are write-once and cannot be removed here. -->
            <div v-if="isAdd && reviewDirectives.length" class="dca-existing">
              <div class="dca-existing-head">Already on this record</div>
              <div v-for="d in reviewDirectives" :key="d.id || d.code" class="dca-existing-chip">
                <span class="dca-existing-name">{{ d.label }}</span>
                <span class="dca-existing-code">{{ codeSystemLabel(d.code_system) }} {{ d.code }}</span>
              </div>
            </div>

            <!-- assign + add modes: shared picker (search, exclusivity, conflict warning) -->
            <DirectivePicker
              v-if="!isReview"
              ref="pickerRef"
              hint=""
              @update:selected="selected = $event"
            />

            <!-- review mode: read-only code cards -->
            <div v-else class="dca-review-list">
              <div v-for="d in reviewDirectives" :key="d.id || d.code" class="dca-review-card">
                <div class="dca-review-name">{{ d.label }}</div>
                <div class="dca-review-code">{{ codeSystemLabel(d.code_system) }} {{ d.code }}</div>
              </div>
              <div v-if="reviewDirectives.length === 0" class="dca-review-empty">
                No coded directives on this record.
              </div>
            </div>
          </div>

          <!-- floating doc-peek button (mobile, codes tab) -->
          <button class="dca-peek-btn" aria-label="Peek at document" @click="peekOpen = true">
            <span class="glyphicon glyphicon-file bi bi-file-earmark-text"></span>
          </button>
        </div>

        <!-- divider (desktop only) -->
        <div class="dca-divider" @mousedown.prevent="onDividerDown">
          <span class="dca-divider-grip"></span>
        </div>

        <!-- RIGHT: document pane -->
        <div class="dca-pane-doc" :class="{ 'dca-mobile-hidden': mobileTab !== 'document' }">
          <DirectivePdfViewer :url="docUrl" :title="docTitle" :content-type="docContentType" />
          <!-- floating jump-to-codes pill (mobile, document tab) -->
          <button class="dca-jump-btn" @click="mobileTab = 'codes'">
            {{ isReview ? 'View directives' : (isAdd ? 'Add codes' : 'Assign codes') }}<template v-if="!isReview && selectedCount"> ({{ selectedCount }})</template>
          </button>
        </div>
      </div>

      <!-- ===== footer ===== -->
      <div class="dca-footer">
        <span v-if="!isReview" class="dca-count">{{ selectedCount }} {{ selectedCount === 1 ? 'code' : 'codes' }} selected</span>
        <span v-else class="dca-count">{{ approverLine }}</span>
        <div class="dca-footer-actions">
          <button v-if="!isReview" class="dca-btn-secondary" data-cy="assign-cancel" @click="onCancel">Cancel</button>
          <button
            v-if="!isReview"
            class="dca-btn-primary"
            data-cy="assign-submit"
            :disabled="selectedCount === 0 || saving"
            @click="onConfirm"
          >
            <span class="glyphicon bi" :class="isAdd ? 'glyphicon-plus bi-plus-lg' : 'glyphicon-pencil bi-pen'"></span>
            {{ isAdd ? 'Add directives' : 'Sign & Activate' }}
          </button>
          <button v-else class="dca-btn-primary" @click="onCancel">Close</button>
        </div>
      </div>

      <!-- ===== mobile peek bottom sheet ===== -->
      <div v-if="peekOpen" class="dca-peek-overlay" @click.self="peekOpen = false">
        <div class="dca-peek-sheet">
          <div class="dca-peek-head">
            <span class="dca-peek-title">{{ docTitle || 'Document' }}</span>
            <button class="dca-close" aria-label="Close preview" @click="peekOpen = false">
              <span class="glyphicon glyphicon-remove bi bi-x-lg"></span>
            </button>
          </div>
          <div class="dca-peek-body">
            <DirectivePdfViewer :url="docUrl" :title="docTitle" :content-type="docContentType" />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
// Full-screen "Assign directive codes" split view: the signer reads the uploaded
// document while assigning codes. Desktop: resizable split (codes | document).
// Mobile (≤700px): Document / Assign-codes tabs + a peek bottom sheet.
// Three modes:
//   'assign' — the signer codes a document-only record as part of Sign & Activate.
//   'add'    — a reviewer (privilege 53, no NPI needed) adds codes before sign-off;
//              same picker, but it only adds and never changes status.
//   'review' — read-only: the record's existing codes beside the document.
// Emits `confirm` with the chosen directives ({ catalog_id, code, label })
// — the parent does the writing; this component never calls the API itself.
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { codeSystemLabel } from "@/api/directivesApi";
import DirectivePicker from "./DirectivePicker.vue";
import DirectivePdfViewer from "./DirectivePdfViewer.vue";

const props = defineProps({
  show: { type: Boolean, default: false },
  mode: { type: String, default: "assign" }, // 'assign' | 'add' | 'review'
  saving: { type: Boolean, default: false },
  patientName: { type: String, default: "" },
  /** Preformatted "name · MRN · DOB" header line from the parent page. */
  patientLine: { type: String, default: "" },
  recordLabel: { type: String, default: "" },
  docUrl: { type: String, default: "" },
  docTitle: { type: String, default: "" },
  // The document row's content type: decides image vs PDF rendering.
  docContentType: { type: String, default: "" },
  // review mode: the record's existing directives, read-only, + approver line.
  // add mode reuses reviewDirectives as the "already on this record" context.
  reviewDirectives: { type: Array, default: () => [] },
  approverLine: { type: String, default: "" },
});
const emit = defineEmits(["cancel", "confirm"]);

const isReview = computed(() => props.mode === "review");
const isAdd = computed(() => props.mode === "add");

const headerTitle = computed(() => {
  if (isReview.value) return "Review directive codes";
  return isAdd.value ? "Add directive codes" : "Assign directive codes";
});

const instruction = computed(() => {
  if (isReview.value) {
    return "Reviewing the directive codes already on file for this record, alongside the source document.";
  }
  if (isAdd.value) {
    // Deliberately does NOT promise activation — adding codes leaves the record
    // exactly where it is; a clinician still has to sign it.
    return props.reviewDirectives.length
      ? "Read the document and add any directives it contains that are not yet coded. This does not sign the record — a clinician still reviews and activates it."
      : "Read the document and select the directives it contains. This does not sign the record — a clinician still reviews and activates it.";
  }
  return "This record has no coded directive yet. Search and select one or more (non-exclusive within a category) to activate it on sign-off.";
});

const pickerRef = ref(null);
const selected = ref([]);
const selectedCount = computed(() => selected.value.length);

const mobileTab = ref("document");
const peekOpen = ref(false);

// Desktop split: left pane width in % (drag-clamped to 28–64).
const leftPct = ref(42);
const splitRef = ref(null);
let dragging = false;

function onDividerDown() {
  const el = splitRef.value;
  if (!el) return;
  dragging = true;
  const rect = el.getBoundingClientRect();
  const move = (ev) => {
    if (!dragging) return;
    const pct = ((ev.clientX - rect.left) / rect.width) * 100;
    leftPct.value = Math.max(28, Math.min(64, pct));
  };
  const up = () => {
    dragging = false;
    window.removeEventListener("mousemove", move);
    window.removeEventListener("mouseup", up);
  };
  window.addEventListener("mousemove", move);
  window.addEventListener("mouseup", up);
}

watch(
  () => props.show,
  (open) => {
    if (open) {
      selected.value = [];
      mobileTab.value = "document";
      peekOpen.value = false;
      pickerRef.value?.reset?.();
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }
);
onBeforeUnmount(() => {
  document.body.style.overflow = "";
});

function onCancel() {
  emit("cancel");
}
function onConfirm() {
  if (selectedCount.value === 0) return;
  emit("confirm", selected.value);
}
</script>

<style scoped>
.dca {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: var(--eh-muted-fill);
  display: flex;
  flex-direction: column;
}
/* ===== header ===== */
.dca-header {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 20px;
  background: var(--eh-white);
  border-bottom: 1px solid var(--eh-divider);
}
.dca-header-title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.dca-header-icon {
  color: var(--eh-teal);
  font-size: 17px;
}
.dca-header-text {
  min-width: 0;
}
.dca-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--eh-teal-ink);
  line-height: 1.2;
}
.dca-subtitle {
  font-size: 12.5px;
  color: var(--eh-muted);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dca-close {
  border: 1px solid var(--eh-divider);
  background: var(--eh-white);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  font-size: 14px;
  color: var(--eh-muted);
  cursor: pointer;
  flex: none;
}
.dca-close:hover {
  background: var(--eh-muted-fill);
  color: var(--eh-teal-ink);
}
/* ===== mobile tabs (hidden on desktop) ===== */
.dca-tabs {
  display: none;
  flex: none;
  gap: 6px;
  padding: 8px 14px;
  background: var(--eh-white);
  border-bottom: 1px solid var(--eh-divider);
}
.dca-tab {
  flex: 1;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: var(--eh-muted-fill);
  color: var(--eh-muted);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.dca-tab.active {
  background: var(--eh-teal);
  color: var(--eh-white);
}
.dca-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.28);
  font-size: 11px;
  font-weight: 700;
}
.dca-tab:not(.active) .dca-tab-badge {
  background: var(--eh-muted-border);
  color: var(--eh-white);
}
/* ===== body split ===== */
.dca-body {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
}
.dca-pane-codes {
  width: var(--dca-left, 42%);
  flex: none;
  min-width: 320px;
  max-width: 64%;
  display: flex;
  flex-direction: column;
  background: var(--eh-white);
  position: relative;
}
.dca-pane-codes-head {
  flex: none;
  padding: 16px 20px 12px;
  border-bottom: 1px solid var(--eh-muted-fill);
}
.dca-instruction {
  font-size: 13px;
  color: var(--eh-muted);
  line-height: 1.5;
}
.dca-approver-line {
  margin-top: 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--eh-teal);
}
.dca-pane-codes-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}
.dca-divider {
  width: 6px;
  flex: none;
  cursor: col-resize;
  background: var(--eh-divider);
  position: relative;
}
.dca-divider:hover {
  background: var(--eh-muted-border);
}
.dca-divider-grip {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 3px;
  height: 36px;
  border-radius: 3px;
  background: var(--eh-muted);
}
.dca-pane-doc {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}
/* ===== add mode: existing codes context ===== */
/* Muted next to the picker below it: this is context, not the thing being
   edited. Write-once codes have no remove affordance by design. */
.dca-existing {
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid var(--eh-divider);
  border-radius: 10px;
  background: var(--eh-muted-fill);
}
.dca-existing-head {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--eh-muted);
  margin-bottom: 8px;
}
.dca-existing-chip {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  padding: 4px 0;
}
.dca-existing-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--eh-teal-ink);
}
.dca-existing-code {
  font-size: 11px;
  color: var(--eh-muted);
  font-family: var(--eh-font-data);
}

/* ===== review mode cards ===== */
.dca-review-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dca-review-card {
  padding: 14px 16px;
  border: 1px solid var(--eh-divider);
  border-left: 3px solid var(--eh-teal);
  border-radius: 10px;
  background: var(--eh-paper);
}
.dca-review-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--eh-teal-ink);
}
.dca-review-code {
  font-size: 12px;
  color: var(--eh-muted);
  margin-top: 3px;
  font-family: var(--eh-font-data);
}
.dca-review-empty {
  padding: 14px 16px;
  border: 1px dashed var(--eh-divider);
  border-radius: 10px;
  font-size: 12.5px;
  color: var(--eh-muted);
}
/* ===== footer ===== */
.dca-footer {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  background: var(--eh-white);
  border-top: 1px solid var(--eh-divider);
}
.dca-count {
  font-size: 13px;
  color: var(--eh-muted);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dca-footer-actions {
  display: flex;
  gap: 10px;
  flex: none;
}
.dca-btn-secondary,
.dca-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}
.dca-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
/* ===== floating mobile buttons (hidden on desktop) ===== */
.dca-peek-btn,
.dca-jump-btn {
  display: none;
}
/* ===== peek bottom sheet ===== */
.dca-peek-overlay {
  position: fixed;
  inset: 0;
  z-index: 5;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: flex-end;
}
.dca-peek-sheet {
  width: 100%;
  height: 58%;
  background: var(--eh-white);
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -8px 30px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.dca-peek-head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--eh-divider);
}
.dca-peek-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--eh-muted);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dca-peek-body {
  flex: 1;
  min-height: 0;
}
/* ===== mobile layout ===== */
@media (max-width: 700px) {
  .dca-tabs {
    display: flex;
  }
  .dca-divider {
    display: none;
  }
  .dca-pane-codes,
  .dca-pane-doc {
    width: 100%;
    max-width: none;
    min-width: 0;
    flex: 1;
  }
  .dca-mobile-hidden {
    display: none;
  }
  .dca-peek-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: 16px;
    bottom: 18px;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    border: none;
    background: var(--eh-white);
    color: var(--eh-teal);
    font-size: 17px;
    cursor: pointer;
    box-shadow: 0 6px 18px rgba(26, 54, 93, 0.22);
    z-index: 3;
  }
  .dca-jump-btn {
    display: inline-flex;
    align-items: center;
    position: absolute;
    right: 16px;
    bottom: 18px;
    height: 42px;
    padding: 0 18px;
    border-radius: 999px;
    border: none;
    background: var(--eh-teal);
    color: var(--eh-white);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 6px 18px rgba(43, 108, 176, 0.4);
    z-index: 3;
  }
  .dca-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  .dca-footer-actions {
    width: 100%;
  }
  .dca-btn-secondary,
  .dca-btn-primary {
    flex: 1;
    justify-content: center;
  }
}
</style>
